import { useEffect } from "react";
import { calculateStudioPrice } from "../util/calculateStudioPrice";
import { calculateCreativePackagesPrice } from "../util/calculateCreativePackagesPrice";
import { getSelectedPackages } from "../util/getSelectedPackages";
import {
    computePaidEquipmentSubtotal,
    deriveBookedEquipmentLines,
} from "../store/bookingStore";

/**
 * Keeps baseStudioPrice, fees, totals, creative/equipment subtotals, bookedEquipment,
 * and totalPrice in sync (same logic as studio profile sidebar booking).
 */
export function useBookingPricingSync({
    bookingStudioData,
    bookingData,
    updateBookingData,
    studioCreativesData,
    isPackagePricing,
}) {
    useEffect(() => {
        if (!bookingStudioData) return;

        const result = calculateStudioPrice({
            bookingStudioData,
            bookingData,
        });

        // Single source of billableHours: the value calculateStudioPrice used for
        // the studio price (Math.max(duration, minNumberOfHours) for HOURLY,
        // packageDurationHours for PACKAGES). Creative pricing must use the SAME value.
        const billableHours = result.billableHours || 0;

        const selectedPackages = getSelectedPackages(
            studioCreativesData,
            bookingData?.creativePackages || []
        );
        // Mirror the Lambda: BASIC creatives bill pricePerHour × billableHours,
        // all other types bill a flat price once.
        const totalCreativePrice = calculateCreativePackagesPrice(
            selectedPackages,
            billableHours
        ).total;

        const totalEquipmentPrice = computePaidEquipmentSubtotal(bookingData?.equipment);

        const feesTotal = (result.appliedFees || []).reduce((sum, fee) => sum + fee.amount, 0);

        const subtotal = result.pricingUnavailable
            ? 0
            : result.totalStudioPrice + feesTotal + totalCreativePrice + totalEquipmentPrice;

        const totalPrice = Math.round(subtotal * 100) / 100;

        updateBookingData("baseStudioPrice", result.base);
        updateBookingData("appliedDiscount", result.appliedDiscount);
        updateBookingData("discountMinHoursApplied", result.discountMinHoursApplied);
        updateBookingData("discountTypeApplied", result.discountTypeApplied);
        updateBookingData("discountValueApplied", result.discountValueApplied ?? null);
        updateBookingData("appliedSurcharge", result.appliedSurcharge);
        updateBookingData("appliedSurchargeMeta", result.appliedSurchargeMeta ?? null);
        updateBookingData("appliedFees", result.appliedFees);
        updateBookingData("totalStudioPrice", result.totalStudioPrice);
        updateBookingData("totalCreativePrice", totalCreativePrice);
        updateBookingData("totalEquipmentPrice", totalEquipmentPrice);
        updateBookingData("bookedEquipment", deriveBookedEquipmentLines(bookingData?.equipment));
        updateBookingData("pricingUnavailable", Boolean(result.pricingUnavailable));
        updateBookingData("pricingUnavailableReason", result.pricingUnavailableReason ?? null);
        updateBookingData("totalPrice", totalPrice);
    }, [
        bookingStudioData,
        bookingData.date,
        bookingData.startTime,
        bookingData.endTime,
        bookingData.packageId,
        bookingData.selectedPeopleGroupKey,
        bookingData.numberOfPeople,
        bookingData.creativePackages,
        bookingData.equipment,
        bookingData.packageDurationHours,
        studioCreativesData,
        isPackagePricing,
        // updateBookingData from context is not referentially stable; omit to avoid re-running every render.
    ]); // eslint-disable-line react-hooks/exhaustive-deps -- updateBookingData intentionally omitted
}
